/**
 * RTDB-Enhanced NHL Service
 * Wraps NHLService with Firebase Realtime Database caching
 * Maintains API compatibility while adding caching layer
 */

import baseNHLService from '../../../../components/dashboards/services/nhlService';
import nhlRTDBCache from './rtdbCache';
import {
  CACHE_PATHS,
  getCacheKey,
  TTL_CONFIG,
  logCache,
  getCurrentSeason
} from './rtdbConfig';

/**
 * NHL Service with RTDB Caching
 *
 * Wraps the base NHL API service with Firebase RTDB caching to:
 * 1. Fetch NHL game data from api-sports.io
 * 2. Cache responses in Firebase RTDB with smart TTL
 * 3. Provide cached data when available
 * 4. Handle cache invalidation based on game status
 */
class RTDBNHLService {
  constructor() {
    this.baseService = baseNHLService;
    this.debug = process.env.NODE_ENV === 'development';
  }

  /**
   * Get NHL games with RTDB caching
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} - API response with games
   */
  async getGames(params = {}) {
    const { id = null, date = null, season = null, team = null, live = null, league = null, timezone = null } = params;

    // Build cache key based on parameters
    let cachePath = null;
    let ttl = TTL_CONFIG.DEFAULT;

    if (live) {
      // Live games - very short cache
      cachePath = `${CACHE_PATHS.GAMES_LIVE}/all`;
      ttl = TTL_CONFIG.LIVE_GAMES;
    } else if (date && !team) {
      // Date-based query without team filter
      const cacheKey = `all_${season || getCurrentSeason()}`;
      cachePath = `${CACHE_PATHS.GAMES_DATES}/${date}/${cacheKey}`;
      ttl = TTL_CONFIG.DATE_GAMES;
    } else if (date && team) {
      // Date-based query with team filter
      const cacheKey = `team_${team}_${season || getCurrentSeason()}`;
      cachePath = `${CACHE_PATHS.GAMES_DATES}/${date}/${cacheKey}`;
      ttl = TTL_CONFIG.DATE_GAMES;
    } else if (id) {
      // Single game query
      cachePath = `${CACHE_PATHS.GAMES}/single/${id}`;
      ttl = TTL_CONFIG.DEFAULT;
    }

    // Try to get from cache if we have a cache path
    if (cachePath) {
      try {
        const cachedData = await nhlRTDBCache.get(cachePath);
        if (cachedData && Array.isArray(cachedData) && cachedData.length > 0) {
          logCache('hit', `Games from cache: ${cachePath}`);
          return cachedData;
        }
      } catch (error) {
        logCache('error', 'Cache read failed, falling back to API', error);
      }
    }

    // Not in cache, fetch from API
    logCache('miss', 'Fetching games from NHL API', params);

    try {
      const response = await this.baseService.getGames(params);

      // Cache the response if we have a cache path and valid data
      if (cachePath && response && response.response) {
        await nhlRTDBCache.setGames(cachePath, response.response, ttl);
      }

      return response;
    } catch (error) {
      logCache('error', 'Failed to fetch games from NHL API', error);
      return {
        get: 'games',
        parameters: params,
        results: 0,
        response: [],
        error: error.message
      };
    }
  }

  /**
   * Get today's NHL games
   * @param {number} league - League ID (optional)
   * @param {string} timezone - Timezone (optional)
   * @returns {Promise<Object>}
   */
  async getTodayGames(league = null, timezone = null) {
    // Get today's date and use cached getGames() for 1:many efficiency
    const today = new Date().toISOString().split('T')[0];
    const currentSeason = getCurrentSeason();
    return await this.getGames({ date: today, league, season: currentSeason, timezone });
  }

  /**
   * Get live NHL games
   * @returns {Promise<Object>}
   */
  async getLiveGames() {
    return await this.getGames({ live: true });
  }

  /**
   * Get games for a specific season
   * @param {string} season - Season identifier (e.g., '2025')
   * @param {string} date - Optional date filter
   * @param {number} league - Optional league ID
   * @param {string} timezone - Optional timezone
   * @returns {Promise<Object>}
   */
  async getGamesForSeason(season, date = null, league = null, timezone = null) {
    return await this.getGames({ season, date, league, timezone });
  }

  /**
   * Get games by date
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {number} league - League ID (optional)
   * @param {string} season - Season (optional)
   * @param {string} timezone - Timezone (optional)
   * @returns {Promise<Object>}
   */
  async getGamesByDate(date, league = null, season = null, timezone = null) {
    // Use the cached getGames() method for consistent caching behavior
    return await this.getGames({ date, league, season, timezone });
  }

  /**
   * Get NHL standings with caching
   * @param {number} league - League ID
   * @param {string} season - Season identifier
   * @param {number} team - Team ID (optional)
   * @returns {Promise<Object>}
   */
  async getStandings(league, season = null, team = null) {
    const currentSeason = season || getCurrentSeason();
    const cacheKey = getCacheKey('standings', league, currentSeason, team);
    const cachePath = `${CACHE_PATHS.STANDINGS}/${cacheKey}`;
    const ttl = TTL_CONFIG.STANDINGS;

    // Try cache first
    try {
      const cachedData = await nhlRTDBCache.get(cachePath);
      if (cachedData) {
        logCache('hit', `Standings from cache: league ${league}, season ${currentSeason}`);
        return cachedData;
      }
    } catch (error) {
      logCache('error', 'Cache read failed for standings', error);
    }

    // Fetch from API
    logCache('miss', 'Fetching standings from NHL API');

    try {
      const response = await this.baseService.getStandings({ league, season: currentSeason, team });

      // Cache the response
      if (response && response.response) {
        await nhlRTDBCache.set(cachePath, response, ttl);
      }

      return response;
    } catch (error) {
      logCache('error', 'Failed to fetch standings from NHL API', error);
      return {
        get: 'standings',
        parameters: { league, season: currentSeason, team },
        results: 0,
        response: [],
        error: error.message
      };
    }
  }

  /**
   * Get player statistics with caching
   * @param {number} playerId - Player ID
   * @param {string} season - Season identifier
   * @returns {Promise<Object>}
   */
  async getPlayerStats(playerId, season = null) {
    if (!playerId) return null;

    const currentSeason = season || getCurrentSeason();
    const cacheKey = getCacheKey('player', playerId, currentSeason);
    const cachePath = `${CACHE_PATHS.PLAYERS}/${cacheKey}`;
    const ttl = TTL_CONFIG.PLAYER_STATS;

    // Try cache first
    try {
      const cachedData = await nhlRTDBCache.get(cachePath);
      if (cachedData) {
        logCache('hit', `Player stats from cache: ${playerId}`);
        return cachedData;
      }
    } catch (error) {
      logCache('error', 'Cache read failed for player stats', error);
    }

    // Fetch from API
    logCache('miss', 'Fetching player stats from NHL API');

    try {
      const response = await this.baseService.getPlayerStatistics({ id: playerId, season: currentSeason });

      // Cache the response
      if (response && response.response) {
        await nhlRTDBCache.set(cachePath, response, ttl);
      }

      return response;
    } catch (error) {
      logCache('error', 'Failed to fetch player stats from NHL API', error);
      return {
        get: 'player',
        parameters: { playerId, season: currentSeason },
        response: null,
        error: error.message
      };
    }
  }

  /**
   * Get teams with caching
   * @param {Object} params - Query parameters (id, name, league, season, country, code, search)
   * @returns {Promise<Object>}
   */
  async getTeams(params = {}) {
    const { id = null, league = null, season = null } = params;

    if (id) {
      // Specific team - shorter cache
      const cacheKey = getCacheKey('team', id);
      const cachePath = `${CACHE_PATHS.TEAMS}/${cacheKey}`;
      const ttl = TTL_CONFIG.TEAMS;

      try {
        const cachedData = await nhlRTDBCache.get(cachePath);
        if (cachedData) {
          logCache('hit', `Team from cache: ${id}`);
          return cachedData;
        }
      } catch (error) {
        logCache('error', 'Cache read failed for team', error);
      }

      logCache('miss', 'Fetching single team from NHL API');

      try {
        const response = await this.baseService.getTeams(params);
        if (response && response.response) {
          await nhlRTDBCache.set(cachePath, response, ttl);
        }
        return response;
      } catch (error) {
        logCache('error', 'Failed to fetch team from NHL API', error);
        return { get: 'teams', response: null, error: error.message };
      }
    }

    // All teams - longer cache
    const currentSeason = season || getCurrentSeason();
    const cacheKey = getCacheKey('teams', league, currentSeason);
    const cachePath = `${CACHE_PATHS.TEAMS}/${cacheKey}`;
    const ttl = TTL_CONFIG.TEAMS;

    // Try cache first
    try {
      const cachedData = await nhlRTDBCache.get(cachePath);
      if (cachedData) {
        logCache('hit', `Teams from cache: league ${league}, season ${currentSeason}`);
        return cachedData;
      }
    } catch (error) {
      logCache('error', 'Cache read failed for teams', error);
    }

    // Fetch from API
    logCache('miss', 'Fetching teams from NHL API');

    try {
      const response = await this.baseService.getTeams({ ...params, season: currentSeason });

      // Cache the response
      if (response && response.response) {
        await nhlRTDBCache.set(cachePath, response, ttl);
      }

      return response;
    } catch (error) {
      logCache('error', 'Failed to fetch teams from NHL API', error);
      return {
        get: 'teams',
        parameters: { ...params, season: currentSeason },
        results: 0,
        response: [],
        error: error.message
      };
    }
  }

  /**
   * Get game events (play-by-play data)
   * @param {number} gameId - Game ID
   * @returns {Promise<Object>}
   */
  async getGameEvents(gameId) {
    if (!gameId) return null;

    logCache('info', 'Fetching game events from NHL API');

    try {
      const response = await this.baseService.getGameEvents(gameId);
      return response;
    } catch (error) {
      logCache('error', 'Failed to fetch game events from NHL API', error);
      return {
        get: 'events',
        parameters: { gameId },
        response: null,
        error: error.message
      };
    }
  }

  /**
   * Get betting odds (if available via NHL API or separate odds API)
   * @param {string} gameId - Game ID
   * @returns {Promise<Object>}
   *
   * TO BE IMPLEMENTED - depends on odds API availability
   */
  async getOdds(gameId) {
    logCache('info', 'Odds fetching - awaiting odds API integration', { gameId });

    // Odds are not cached due to frequent changes
    // Direct API call when implemented

    return {
      get: 'odds',
      parameters: { gameId },
      response: null
    };
  }

  // Utility methods

  /**
   * Get current NHL season
   * @returns {string} Season identifier (e.g., '2024-25')
   */
  getCurrentSeason() {
    return getCurrentSeason();
  }

  /**
   * Get today's date in YYYY-MM-DD format
   * @returns {string}
   */
  getTodayDate() {
    return new Date().toISOString().split('T')[0];
  }

  /**
   * Clear all NHL cache
   */
  clearCache() {
    nhlRTDBCache.clear(CACHE_PATHS.ROOT);
    logCache('info', 'All NHL caches cleared');
  }

  /**
   * Get cache statistics
   * @returns {Promise<Object>}
   */
  async getCacheStats() {
    return await nhlRTDBCache.getStats();
  }

  /**
   * Set API key (if needed for NHL API)
   * @param {string} key - API key
   */
  setApiKey(key) {
    this.apiKey = key;
  }
}

// Create singleton instance
const rtdbNHLService = new RTDBNHLService();

// Export as default
export default rtdbNHLService;

// Also export as named for explicit usage
export { rtdbNHLService };

// Browser debugging helper
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.NHLRTDBService = {
    instance: rtdbNHLService,
    getCacheStats: () => rtdbNHLService.getCacheStats(),
    clearCache: () => rtdbNHLService.clearCache()
  };

  console.log('🏒 NHL RTDB Service loaded (awaiting API integration)');
  console.log('Access via window.NHLRTDBService for debugging');
}
